import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Typography,
  Box,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  Lightbulb,
  CheckCircle,
  RadioButtonUnchecked,
  ArrowForward
} from '@mui/icons-material';
import { brand, text } from '../../theme/colors';

const DISMISS_KEY = 'eqaya_client_getting_started_dismissed';

/**
 * Getting Started checklist for Project Owners.
 * Shown on the dashboard until all items are complete or user dismisses it.
 *
 * Props:
 *   profileComplete   – boolean: has the user filled in name, phone, location?
 *   hasProject        – boolean: has the user created at least one project?
 *   hasEscrow         – boolean: has the user funded their payment wallet?
 *   hasContractor     – boolean: has the user hired or invited a contractor?
 *   onCreateProject   – callback: opens the project wizard
 */
const ClientGettingStartedChecklist = ({
  profileComplete = false,
  hasProject = false,
  hasEscrow = false,
  hasContractor = false,
  onCreateProject
}) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === 'true'
  );

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
  };

  if (dismissed) return null;

  const items = [
    {
      label: 'Complete your profile',
      description: 'Add your name, phone number, and location',
      link: '/profile',
      checked: profileComplete,
      action: () => navigate('/profile')
    },
    {
      label: 'Create your first project',
      description: 'Describe what you want built and where',
      link: null,
      checked: hasProject,
      action: onCreateProject || (() => navigate('/projects'))
    },
    {
      label: 'Fund your project wallet',
      description: 'Secure your funds in escrow — pay only when work is done',
      link: '/payments',
      checked: hasEscrow,
      action: () => navigate('/payments')
    },
    {
      label: 'Find & hire a contractor',
      description: 'Browse verified contractors or invite someone you know',
      link: '/contractors',
      checked: hasContractor,
      action: () => navigate('/contractors')
    }
  ];

  const completedCount = items.filter(i => i.checked).length;
  const allDone = completedCount === items.length;
  const progress = (completedCount / items.length) * 100;

  if (allDone) {
    // Auto-dismiss when all steps are done
    handleDismiss();
    return null;
  }

  return (
    <Card
      sx={{
        backgroundColor: 'white',
        borderLeft: `4px solid ${brand.primary}`,
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        mb: 3
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Lightbulb sx={{ color: brand.primary }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: text.primary }}>
              Get Started
            </Typography>
          </Box>
          <Chip
            label={`${completedCount} of ${items.length} done`}
            size="small"
            sx={{
              bgcolor: completedCount > 0 ? '#e8f5e9' : '#f5f5f5',
              color: completedCount > 0 ? '#2e7d32' : '#757575',
              fontWeight: 600
            }}
          />
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            mb: 2,
            height: 6,
            borderRadius: 3,
            bgcolor: '#e8eaf6',
            '& .MuiLinearProgress-bar': { bgcolor: brand.primary, borderRadius: 3 }
          }}
        />

        <List disablePadding>
          {items.map((item, index) => (
            <ListItem
              button
              key={index}
              onClick={item.action}
              sx={{
                borderRadius: '8px',
                py: 0.75,
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {item.checked ? (
                  <CheckCircle sx={{ color: brand.primary }} />
                ) : (
                  <RadioButtonUnchecked sx={{ color: '#9e9e9e' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                secondary={!item.checked ? item.description : null}
                primaryTypographyProps={{
                  sx: {
                    textDecoration: item.checked ? 'line-through' : 'none',
                    color: item.checked ? '#9e9e9e' : text.primary,
                    fontWeight: item.checked ? 400 : 600,
                    fontSize: '0.9rem'
                  }
                }}
                secondaryTypographyProps={{
                  sx: { fontSize: '0.78rem', color: text.secondary }
                }}
              />
              {!item.checked && (
                <ArrowForward sx={{ fontSize: 16, color: '#9e9e9e', flexShrink: 0 }} />
              )}
            </ListItem>
          ))}
        </List>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            onClick={handleDismiss}
            sx={{ color: text.secondary, textTransform: 'none', fontSize: '0.8rem' }}
          >
            Dismiss
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ClientGettingStartedChecklist;
